import { CalendarRange, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Section } from '../ui/Collapsible';
import { useParent } from './ParentContext';
import { SCHOOL_CALENDAR_CHANGED } from './SchoolCalendar';

type Mode = 'quarters' | 'semesters' | 'custom';
interface Period {
  name: string;
  start: string;
  end: string;
}
interface Schedule {
  mode: Mode;
  custom: Period[];
  schoolYear: { start: string; end: string } | null;
  periods: (Period & { available: boolean; schoolDays?: number })[];
}

const MODES: { id: Mode; label: string }[] = [
  { id: 'quarters', label: 'Quarters (9 weeks each)' },
  { id: 'semesters', label: 'Semesters' },
  { id: 'custom', label: 'My own periods' },
];

const pretty = (ymd: string) => new Date(`${ymd}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

/**
 * The report card schedule (#299): when report cards come out. Quarters (9 weeks of school days) and semesters follow
 * the school year and skip its holidays and breaks (#359, #365); families with extended or year-round schedules set their
 * own periods. A period's report card can be downloaded from the Grade Book once it has ended.
 */
export function ReportPeriodsSettings({ embedded = false }: { embedded?: boolean }) {
  const { toast } = useParent();
  const [s, setS] = useState<Schedule | null>(null);
  const [custom, setCustom] = useState<Period[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const load = (first: boolean) =>
      api
        .get<Schedule>('/api/report-periods')
        .then((r) => {
          setS(r);
          if (first) setCustom(r.custom); // don't throw away periods the parent is still typing
        })
        .catch((e) => toast({ text: (e as Error).message, tone: 'alert' }));
    load(true);
    // The dates skip the school calendar's breaks, so refresh when it changes on this page (#359, #365).
    const onChange = () => void load(false);
    window.addEventListener(SCHOOL_CALENDAR_CHANGED, onChange);
    return () => window.removeEventListener(SCHOOL_CALENDAR_CHANGED, onChange);
  }, [toast]);

  async function save(mode: Mode, periods: Period[] = custom) {
    setBusy(true);
    try {
      const r = await api.put<Schedule>('/api/report-periods', { mode, custom: periods.filter((p) => p.name.trim() && p.start && p.end) });
      setS(r);
      setCustom(r.custom);
      toast({ text: 'Report card schedule saved.', tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  if (!s) return null;
  const update = (i: number, patch: Partial<Period>) => setCustom(custom.map((p, j) => (j === i ? { ...p, ...patch } : p)));

  return (
    <Section embedded={embedded} id="report-periods" title="Report card schedule" icon={<CalendarRange size={18} />} summary={MODES.find((m) => m.id === s.mode)?.label}>
      <p className="muted small">
        When report cards come out. Each one is ready to download from the Grade Book once its period ends. Quarters are 9 weeks of school days each and
        semesters split the school days in half; both skip the holidays and breaks on your school calendar, so a break moves the dates later. Use your own
        periods for an extended or year-round schedule.
      </p>
      <div className="segmented" role="radiogroup" aria-label="Report card schedule">
        {MODES.map((m) => (
          <button key={m.id} role="radio" aria-checked={s.mode === m.id} className={s.mode === m.id ? 'active' : ''} disabled={busy} onClick={() => save(m.id)}>
            {m.label}
          </button>
        ))}
      </div>
      {s.mode !== 'custom' && !s.schoolYear && <p className="notice">Set the school year above so ClassGuardian can work out the {s.mode}.</p>}
      {s.mode === 'custom' ? (
        <div className="stack">
          {custom.map((p, i) => (
            <div key={i} className="field-row period-row">
              <label className="grow">
                Name
                <input value={p.name} maxLength={40} onChange={(e) => update(i, { name: e.target.value })} placeholder="e.g. Term 1" />
              </label>
              <label>
                From
                <input type="date" value={p.start} onChange={(e) => update(i, { start: e.target.value })} />
              </label>
              <label>
                To
                <input type="date" value={p.end} min={p.start} onChange={(e) => update(i, { end: e.target.value })} />
              </label>
              <button className="icon-btn" onClick={() => setCustom(custom.filter((_, j) => j !== i))} aria-label={`Remove ${p.name || 'period'}`}>
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          <div className="row-between">
            <button className="btn small" onClick={() => setCustom([...custom, { name: `Term ${custom.length + 1}`, start: '', end: '' }])} disabled={custom.length >= 20}>
              <Plus size={15} /> Add a period
            </button>
            <button className="btn primary small" disabled={busy} onClick={() => save('custom')}>
              Save periods
            </button>
          </div>
        </div>
      ) : (
        s.periods.length > 0 && (
          <ul className="period-list">
            {s.periods.map((p) => (
              <li key={p.name}>
                <strong>{p.name}</strong>
                <span className="muted small">
                  {pretty(p.start)} – {pretty(p.end)}
                  {p.schoolDays !== undefined && ` · ${p.schoolDays} school day${p.schoolDays === 1 ? '' : 's'}`} · {p.available ? 'report card ready' : `ready after ${pretty(p.end)}`}
                </span>
              </li>
            ))}
          </ul>
        )
      )}
    </Section>
  );
}
